import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import { FORGOT_PASSWORD_STEPS } from '../../constants/authConstants';

function ForgotPasswordModal({ show, onHide, onSuccess }) {
  const { t } = useTranslation();

  const [step, setStep] = useState(FORGOT_PASSWORD_STEPS.REQUEST_CODE);
  const [identifier, setIdentifier] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoNotice, setInfoNotice] = useState('');
  const [showPass, setShowPass] = useState(false);

  const resetState = () => {
    setStep(FORGOT_PASSWORD_STEPS.REQUEST_CODE);
    setIdentifier('');
    setUserEmail('');
    setCode('');
    setPassword('');
    setPasswordConfirmation('');
    setError('');
    setInfoNotice('');
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  // Step 1: Send Recovery Code (BA-01, FE-01)
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/reset-password', {
        email: identifier,
        rif: identifier
      });

      setUserEmail(response.data?.email || identifier);
      setStep(FORGOT_PASSWORD_STEPS.VERIFY_CODE);
    } catch (err) {
      setUserEmail(identifier);
      setStep(FORGOT_PASSWORD_STEPS.VERIFY_CODE);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify 6-digit code OTP (BA-02, FE-02)
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) {
      setError(t('code_6_digits_error', 'El código debe tener exactamente 6 dígitos.'));
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post('/auth/reset-password/verify', {
        email: userEmail,
        code
      });

      setStep(FORGOT_PASSWORD_STEPS.NEW_PASSWORD);
    } catch (err) {
      const errData = err.response?.data;
      if (err.response?.status === 429) {
        setError(errData?.message || t('code_expired_error'));
      } else {
        setError(errData?.message || t('code_invalid_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Confirm Password Reset (BA-03, FE-02)
  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('min_6_chars_error', 'La contraseña debe tener al menos 6 caracteres.'));
      return;
    }

    if (password !== passwordConfirmation) {
      setError(t('passwords_not_match', 'Las contraseñas no coinciden.'));
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/reset-password/confirm', {
        email: userEmail,
        code,
        password,
        password_confirmation: passwordConfirmation
      });

      const successMsg = response.data?.message || t('password_updated_success');
      resetState();
      onSuccess(successMsg);
    } catch (err) {
      setError(err.response?.data?.message || t('error', 'Error al actualizar contraseña.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
        <Modal.Title className="h5 font-weight-bold">
          <i className="mdi mdi-lock-reset me-2"></i>
          {step === FORGOT_PASSWORD_STEPS.NEW_PASSWORD 
            ? t('new_password_title', 'Nueva Contraseña') 
            : t('forgot_password_title', 'Recuperación de Contraseña')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#ffffff', padding: '1.75rem' }}>

        {error && (
          <Alert variant="danger" className="mb-3 border-0 fw-bold d-flex align-items-center shadow-sm" style={{ borderRadius: '12px', backgroundColor: '#fee2e2', color: '#991b1b' }}>
            <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.4rem' }}></i>
            <span>{error}</span>
          </Alert>
        )}

        {/* STEP 1: REQUEST CODE */}
        {step === FORGOT_PASSWORD_STEPS.REQUEST_CODE && (
          <Form onSubmit={handleRequestCode}>
            <p className="text-muted small mb-4" style={{ fontSize: '0.9rem' }}>
              {t('forgot_password_subtitle', 'Ingresa tu correo electrónico o RIF registrado para recibir un código de verificación de 6 dígitos.')}
            </p>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-dark small">{t('enter_email_or_rif', 'Correo electrónico o RIF registrado')} *</Form.Label>
              <InputGroup className="shadow-sm d-flex align-items-center" style={{ borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                <InputGroup.Text style={{ backgroundColor: '#ffffff', border: 'none', padding: '0.6rem 0.6rem 0.6rem 0.85rem', display: 'flex', alignItems: 'center' }}>
                  <i className="mdi mdi-account-card-details-outline" style={{ color: '#183eeb', fontSize: '1.2rem' }}></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  required
                  placeholder="ejemplo@rutex.com o J-12345678-9"
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  style={{ border: 'none', boxShadow: 'none', padding: '0.6rem 0.85rem 0.6rem 0.25rem', color: '#0f172a', fontWeight: '500', backgroundColor: '#ffffff' }}
                />
              </InputGroup>
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                type="submit" 
                style={{ 
                  borderRadius: '10px', 
                  background: 'linear-gradient(135deg, #183eeb 0%, #00024f 100%)', 
                  border: 'none',
                  padding: '0.75rem' 
                }} 
                disabled={loading} 
                className="fw-bold text-white shadow-sm"
              >
                {loading ? <Spinner size="sm" animation="border" /> : t('btn_send_recovery_code', 'Enviar Código de Recuperación')}
              </Button>
            </div>
          </Form>
        )}

        {/* STEP 2: VERIFY 6-DIGIT CODE */}
        {step === FORGOT_PASSWORD_STEPS.VERIFY_CODE && (
          <Form onSubmit={handleVerifyCode}>
            <p className="text-muted small mb-3" style={{ fontSize: '0.9rem' }}>
              {t('enter_code_sent_notice', 'Ingresa el código de 6 dígitos que fue enviado a tu dirección registrada para continuar.')}
            </p>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-dark small">{t('enter_6_digit_code', 'Código de Verificación (6 dígitos)')} *</Form.Label>
              <Form.Control
                type="text"
                maxLength={6}
                required
                placeholder="123456"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center font-weight-bold shadow-sm"
                style={{ borderRadius: '10px', border: '2px solid #183eeb', fontSize: '1.6rem', letterSpacing: '8px', padding: '0.6rem' }}
              />
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                type="submit" 
                style={{ 
                  borderRadius: '10px', 
                  background: 'linear-gradient(135deg, #183eeb 0%, #00024f 100%)', 
                  border: 'none',
                  padding: '0.75rem' 
                }} 
                disabled={loading} 
                className="fw-bold text-white shadow-sm"
              >
                {loading ? <Spinner size="sm" animation="border" /> : t('btn_verify_code', 'Verificar Código')}
              </Button>
              <Button variant="link" size="sm" onClick={() => setStep(FORGOT_PASSWORD_STEPS.REQUEST_CODE)} className="text-decoration-none text-muted mt-1">
                {t('request_code_again', 'Volver a solicitar código')}
              </Button>
            </div>
          </Form>
        )}

        {/* STEP 3: NEW PASSWORD */}
        {step === FORGOT_PASSWORD_STEPS.NEW_PASSWORD && (
          <Form onSubmit={handleConfirmReset}>
            <p className="text-muted small mb-3" style={{ fontSize: '0.9rem' }}>
              {t('new_password_subtitle', 'Define tu nueva clave de acceso de al menos 6 caracteres.')}
            </p>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark small">{t('new_password_label', 'Nueva Contraseña')} *</Form.Label>
              <InputGroup className="shadow-sm d-flex align-items-center" style={{ borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                <InputGroup.Text style={{ backgroundColor: '#ffffff', border: 'none', padding: '0.6rem 0.6rem 0.6rem 0.85rem', display: 'flex', alignItems: 'center' }}>
                  <i className="mdi mdi-lock-outline" style={{ color: '#183eeb', fontSize: '1.2rem' }}></i>
                </InputGroup.Text>
                <Form.Control
                  type={showPass ? 'text' : 'password'}
                  required
                  minLength={6}
                  placeholder="******"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ border: 'none', boxShadow: 'none', padding: '0.6rem 0.85rem 0.6rem 0.25rem', color: '#0f172a', fontWeight: '500', backgroundColor: '#ffffff' }}
                />
                <Button 
                  type="button"
                  variant="light"
                  onClick={() => setShowPass(!showPass)} 
                  style={{ 
                    border: 'none', 
                    backgroundColor: '#ffffff', 
                    color: showPass ? '#183eeb' : '#64748b',
                    padding: '0.6rem 0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  <i className={showPass ? 'mdi mdi-eye-off' : 'mdi mdi-eye'} style={{ fontSize: '1.2rem' }}></i>
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-dark small">{t('confirm_password_label', 'Confirmar Nueva Contraseña')} *</Form.Label>
              <InputGroup className="shadow-sm d-flex align-items-center" style={{ borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                <InputGroup.Text style={{ backgroundColor: '#ffffff', border: 'none', padding: '0.6rem 0.6rem 0.6rem 0.85rem', display: 'flex', alignItems: 'center' }}>
                  <i className="mdi mdi-lock-check-outline" style={{ color: '#183eeb', fontSize: '1.2rem' }}></i>
                </InputGroup.Text>
                <Form.Control
                  type={showPass ? 'text' : 'password'}
                  required
                  minLength={6}
                  placeholder="******"
                  value={passwordConfirmation}
                  onChange={e => setPasswordConfirmation(e.target.value)}
                  style={{ border: 'none', boxShadow: 'none', padding: '0.6rem 0.85rem 0.6rem 0.25rem', color: '#0f172a', fontWeight: '500', backgroundColor: '#ffffff' }}
                />
              </InputGroup>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                type="submit" 
                style={{ 
                  borderRadius: '12px', 
                  backgroundColor: '#198754', 
                  borderColor: '#198754', 
                  padding: '0.85rem' 
                }} 
                disabled={loading} 
                className="fw-bold text-white shadow-sm"
              >
                {loading ? <Spinner size="sm" animation="border" /> : t('btn_reset_password', 'Restablecer Contraseña')}
              </Button>
            </div>
          </Form>
        )}

      </Modal.Body>
    </Modal>
  );
}

export default ForgotPasswordModal;
